/**
 * W-7 · Rapports.
 *
 * Un rapport est ce qui SORT du back-office : c'est le texte envoyé au TROC et
 * au COJOJ. D'où l'aperçu rendu — on relit avant de diffuser — et les deux
 * exports servis par le serveur (`.md` pour un message, `.csv` pour un tableur),
 * jamais reconstitués côté client : le fichier téléchargé doit être exactement
 * celui que l'API produit.
 *
 * Bloc 2 UI/UX (2026-10-14) : la page s'ouvre sur le DERNIER rapport, affiché
 * d'office — c'est lui qu'on vient lire. La liste des précédents vient dessous,
 * et le formulaire de génération, qui ne sert qu'à rattraper une date, est
 * replié tout en bas. Les boutons disent le geste, pas le format : « Lire »,
 * « Télécharger le texte », « Ouvrir dans Excel ».
 */

import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { ReportSummary } from '../lib/api';
import { toast } from '../components/Toast';
import { useApi } from '../lib/useApi';
import { formatDateTime, formatLongDay, toDateInput } from '../lib/format';
import { renderMarkdown } from '../lib/markdown';
import { Async, Badge, Banner, Card, Loading } from '../components/ui';
import { READ_ONLY_NOTICE, useCan } from '../lib/session';

const KINDS: { key: string; label: string; one: string }[] = [
  { key: 'daily', label: 'Journalier', one: 'journalier' },
  { key: 'weekly', label: 'Hebdomadaire', one: 'hebdomadaire' },
  { key: 'custom', label: 'Personnalisé', one: 'personnalisé' },
];

/** « Rapport du lundi 24 août 2026 », « Semaine du 17 au 23 août 2026 » : la période, en toutes lettres. */
function periodTitle(report: ReportSummary): string {
  if (report.kind === 'daily') return `Rapport du ${formatLongDay(report.periodStart)}`;
  const prefix = report.kind === 'weekly' ? 'Semaine du' : 'Du';
  return `${prefix} ${formatLongDay(report.periodStart)} au ${formatLongDay(report.periodEnd)}`;
}

export function ReportsPage(): React.ReactElement {
  // La lecture et le téléchargement restent ouverts à tous : un rapport est
  // déjà agrégé et anonyme, le cacher à un `viewer` n'aurait aucun sens. Seule
  // la GÉNÉRATION est réservée — elle écrase le rapport existant d'une date.
  const canGenerate = useCan('reports:generate');
  const [kind, setKind] = useState('daily');
  const [date, setDate] = useState(toDateInput(new Date()));
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const list = useApi((s) => api.reports(kind, 30, s), `reports|${kind}`);
  const detail = useApi(
    (s) => (selected === null ? Promise.reject(new Error('none')) : api.report(selected, s)),
    `report|${selected ?? ''}`,
    { enabled: selected !== null },
  );

  // Le dernier rapport s'affiche d'office ; au changement de type, le dernier
  // du nouveau type prend la place. Un rapport qui a disparu de la liste n'est
  // jamais gardé à l'écran.
  useEffect(() => {
    const rows = list.data;
    if (!rows) return;
    if (rows.length === 0) {
      setSelected(null);
      return;
    }
    if (selected === null || !rows.some((r) => r.id === selected)) setSelected(rows[0]?.id ?? null);
  }, [list.data, selected]);

  const kindMeta = KINDS.find((k) => k.key === kind) ?? KINDS[0];
  const current = list.data?.find((r) => r.id === selected) ?? null;

  const generate = async (): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      const created = await api.generateReport(kind, date);
      toast(`Rapport ${kindMeta?.one ?? kind} du ${formatLongDay(`${date}T12:00:00Z`)} généré.`);
      setSelected(created.id);
      list.reload();
    } catch (cause) {
      setActionError(cause instanceof ApiError ? cause : new ApiError(0, { title: String(cause) }, true));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Téléchargement : on récupère le contenu via le client (qui envoie le cookie
   * de session) puis on fabrique un `Blob`. Depuis O-3 un simple lien `href`
   * FONCTIONNERAIT — le navigateur joint le cookie tout seul — mais on garde le
   * passage par le client : lui seul sait transformer un 401 en retour à
   * l'écran de connexion, là où un lien direct afficherait une page d'erreur
   * JSON brute dans un onglet.
   */
  const download = async (id: string, format: 'md' | 'csv'): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      const content = await api.reportExport(id, format);
      const blob = new Blob([content], {
        type: format === 'md' ? 'text/markdown;charset=utf-8' : 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `yogmove-rapport-${id}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setActionError(cause instanceof ApiError ? cause : new ApiError(0, { title: String(cause) }, true));
    } finally {
      setBusy(false);
    }
  };

  const noneYet = list.data !== null && list.data.length === 0;

  return (
    <div className="stack">
      {actionError ? (
        <Banner tone="bad" title="Ça n'a pas marché">
          {actionError.userMessage}
          {actionError.problem.detail ? ` : ${actionError.problem.detail}` : ''}
        </Banner>
      ) : null}

      {/* ── Le rapport qu'on vient lire : le dernier, affiché d'office ── */}
      <Card
        title={current ? periodTitle(current) : `Dernier rapport ${kindMeta?.one ?? ''}`}
        hint={
          current
            ? `Généré le ${formatDateTime(current.generatedAt)}. Le rapport tel qu'il sera envoyé : relisez-le ici avant de le diffuser.`
            : 'Le rapport tel qu’il sera envoyé, à relire avant de le diffuser.'
        }
        action={
          current ? (
            <span className="row">
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void download(current.id, 'md')}>
                Télécharger le texte
              </button>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void download(current.id, 'csv')}>
                Ouvrir dans Excel
              </button>
            </span>
          ) : undefined
        }
      >
        {noneYet ? (
          <div className="state state-empty">
            <span className="state-title">Aucun rapport {kindMeta?.one} pour l'instant</span>
            <span>
              {kind === 'daily'
                ? 'Le rapport de la journée se génère tout seul à 23 h 59, heure de Dakar. Pour une date passée, utilisez « Générer un rapport à la main » en bas de page.'
                : 'Utilisez « Générer un rapport à la main » en bas de page.'}
            </span>
          </div>
        ) : selected === null ? (
          <Loading label="Ouverture du dernier rapport…" />
        ) : (
          <Async state={detail}>
            {(report) =>
              // `useApi` garde le rapport précédent pendant le chargement du
              // suivant : sans ce test, l'ancien texte s'afficherait sous le nouveau titre.
              report.id !== selected ? (
                <Loading label="Ouverture du rapport…" />
              ) : report.summaryMd.trim() === '' ? (
                <div className="state state-empty">
                  <span className="state-title">Rapport vide</span>
                  <span>Ce rapport ne contient pas de texte.</span>
                </div>
              ) : (
                <div className="md">{renderMarkdown(report.summaryMd)}</div>
              )
            }
          </Async>
        )}
      </Card>

      {/* ── Les précédents ── */}
      <Card
        title="Rapports précédents"
        hint="Les 30 derniers, du plus récent au plus ancien."
        action={
          <div className="seg" role="group" aria-label="Type de rapport">
            {KINDS.map((k) => (
              <button key={k.key} type="button" aria-pressed={kind === k.key} onClick={() => setKind(k.key)}>
                {k.label}
              </button>
            ))}
          </div>
        }
      >
        <Async state={list}>
          {(rows) =>
            rows.length === 0 ? (
              <p className="muted">Aucun rapport {kindMeta?.one} n'a encore été généré.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Période</th>
                      <th>Généré le</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className={r.id === selected ? 'is-selected' : undefined}>
                        <td>{formatLongDay(r.periodStart)}</td>
                        <td className="mono small">{formatDateTime(r.generatedAt)}</td>
                        <td>
                          <span className="row" style={{ justifyContent: 'flex-end' }}>
                            {r.id === selected ? (
                              <Badge tone="info">affiché</Badge>
                            ) : (
                              <button type="button" className="btn btn-sm" onClick={() => setSelected(r.id)}>
                                Lire
                              </button>
                            )}
                            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void download(r.id, 'csv')}>
                              Ouvrir dans Excel
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
      </Card>

      {/* ── Rattraper une date : rare, donc replié (ouvert s'il n'y a encore rien) ── */}
      <details className="fold" open={noneYet || undefined}>
        <summary>Générer un rapport à la main</summary>
        <div className="fold-body">
          <p className="small muted" style={{ margin: '0 0 12px' }}>
            Le rapport journalier se génère tout seul chaque soir. Ici, vous rattrapez une date : un « rapport du 3 novembre » couvre 00:00 → 23:59
            heure de Dakar, et regénérer une date remplace le rapport existant.
          </p>
          <div className="toolbar">
            <label className="field">
              Type
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                {KINDS.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Date
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              aria-busy={busy}
              disabled={busy || !canGenerate}
              title={canGenerate ? undefined : READ_ONLY_NOTICE}
              onClick={() => void generate()}
            >
              {busy ? 'Génération…' : 'Générer'}
            </button>
          </div>
          {/* Le bouton est désactivé plutôt que masqué : un opérateur qui cherche
              « où est passé Générer ? » perd plus de temps qu'un opérateur à qui
              on dit pourquoi c'est grisé. */}
          {canGenerate ? null : (
            <p className="small muted" style={{ margin: '10px 0 0' }}>
              {READ_ONLY_NOTICE} La lecture et les téléchargements restent disponibles.
            </p>
          )}
        </div>
      </details>
    </div>
  );
}
